import React, { useEffect, useState, useMemo, useRef } from "react";
import { apiGet } from "../../utils/api.js";
import "./screenSessions.css";

const DEFAULT_FILTERS = {
  limit: 20,
  skip: 0,
  status: "",
  agentId: "",
  endReason: "",
  startFrom: "",
  startTo: "",
  q: "",
  sortBy: "startTime",
  order: "desc",
};

function formatDate(value) {
  try {
    return value ? new Date(value).toLocaleString() : "-";
  } catch {
    return "-";
  }
}

export default function ScreenSessions() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({ limit: 20, skip: 0 });
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    timeouts: 0,
    avgDurationSec: 0,
  });
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const pcRef = useRef(null);
  const pendingCandidates = useRef([]);
  const videoRef = useRef(null);
  const [connectionState, setConnectionState] = useState("idle");

  const hasFilters = useMemo(() => {
    return [""];
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      const response = await apiGet(
        `/api/screen-sessions?${params.toString()}`,
      );
      if (!response.ok) {
        throw new Error("Failed to load screen sessions");
      }
      const payload = await response.json();
      if (payload.success) {
        setSessions(payload.data);
        setPagination(
          payload.pagination || { limit: filters.limit, skip: filters.skip },
        );
        setSummary(payload.summary || summary);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnostics = async () => {
    try {
      const response = await apiGet("/api/screen-sessions/diagnostics");
      if (response.ok) {
        const payload = await response.json();
        setDiagnostics(payload.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchDiagnostics();
  }, []);

  const applyFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, skip: 0 }));
  };

  useEffect(() => {
    fetchSessions();
  }, [filters]);

  const goPage = (direction) => {
    setFilters((prev) => {
      const delta = direction === "next" ? prev.limit : -prev.limit;
      const nextSkip = Math.max(prev.skip + delta, 0);
      return { ...prev, skip: nextSkip };
    });
  };

  const statusBadge = (session) => {
    if (session.isActive)
      return <span className="badge badge-active">Active</span>;
    return <span className="badge badge-ended">{session.status}</span>;
  };

  return (
    <div className="screen-sessions-page">
      <header className="screen-sessions-header">
        <h1>Screen Sessions</h1>
        <div className="screen-sessions-actions">
          {diagnostics && (
            <React.Fragment>
              <div className="diag-card">
                <p>Active</p>
                <strong>{diagnostics.activeCount}</strong>
              </div>
              <div className="diag-card">
                <p>Stale Active</p>
                <strong>{diagnostics.staleActiveCount}</strong>
              </div>
              <div className="diag-card">
                <p>Timeouts (last 30m)</p>
                <strong>{summary.timeouts || diagnostics.timeouts || 0}</strong>
              </div>
            </React.Fragment>
          )}
        </div>
      </header>

      <section className="screen-sessions-filters">
        <label>
          Agent ID
          <input
            type="text"
            value={filters.agentId}
            onChange={(e) => applyFilter("agentId", e.target.value)}
            placeholder="Exact agent id"
          />
        </label>
        <label>
          Status
          <select
            value={filters.status}
            onChange={(e) => applyFilter("status", e.target.value)}
          >
            <option value="">Any</option>
            <option value="active">Active</option>
            <option value="ended">Ended</option>
            <option value="terminated">Terminated</option>
            <option value="timeout">Timeout</option>
          </select>
        </label>
        <label>
          Start from
          <input
            type="datetime-local"
            value={filters.startFrom}
            onChange={(e) => applyFilter("startFrom", e.target.value)}
          />
        </label>
        <label>
          Start to
          <input
            type="datetime-local"
            value={filters.startTo}
            onChange={(e) => applyFilter("startTo", e.target.value)}
          />
        </label>
        <label>
          Search
          <input
            type="text"
            value={filters.q}
            onChange={(e) => applyFilter("q", e.target.value)}
            placeholder="Session or agent"
          />
        </label>
      </section>

      <section className="screen-sessions-table">
        <div className="table-actions">
          <p>
            Showing {sessions.length} of {summary.total} sessions (&approx;
            {summary.avgDurationSec}s average)
          </p>
          <div>
            <button onClick={() => goPage("prev")}>Previous</button>
            <button onClick={() => goPage("next")}>Next</button>
          </div>
        </div>
        <div className="screen-sessions-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Session ID</th>
              <th>Agent ID</th>
              <th>Started</th>
              <th>Ended</th>
              <th>Duration</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">Loading...</td>
              </tr>
            ) : sessions.length === 0 ? (
              <tr>
                <td colSpan="7">No sessions matching filters</td>
              </tr>
            ) : (
              sessions.map((session) => (
                <tr key={session.sessionId}>
                  <td>{statusBadge(session)}</td>
                  <td>{session.sessionId}</td>
                  <td>{session.agentId}</td>
                  <td>{formatDate(session.startTime)}</td>
                  <td>{formatDate(session.endTime)}</td>
                  <td>{session.durationLabel}</td>
                  <td>{session.adminId || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        <div className="screen-session-player">
          <video
            ref={videoRef}
            id="remoteVideo"
            autoPlay
            playsInline
            muted
          ></video>
          <p className="connection-state">Connection: {connectionState}</p>
        </div>
      </section>
    </div>
  );
}
